import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-fashion-shop";
const TITLE = "Shopify Fashion Shop: Varianten, Größen, Retouren";
const DESCRIPTION =
  "Shopify für Mode und Bekleidung: Varianten und Größen sauber aufbauen, Retouren senken, mobile Kaufwege verkürzen. Umsetzung als Freelancer im DACH-Raum.";

const faqs = [
  {
    q: "Eignet sich Shopify für einen Modeshop?",
    a: "Ja. Varianten, Größen, Farben, Kollektionen und Sale-Logik lassen sich abbilden, und der Kaufabschluss ist auf dem Handy solide. Aufwand entsteht vor allem bei Größenlogik, Sortimentsstruktur und Bildpflege.",
  },
  {
    q: "Wie viele Varianten sind möglich?",
    a: "Shopify erlaubt pro Produkt mehrere Optionen wie Größe und Farbe mit einer plantarifabhängigen Obergrenze an Kombinationen. Bei sehr großen Größenläufen ist die Sortimentsstruktur wichtiger als die reine Zahl.",
  },
  {
    q: "Wie lassen sich Retouren senken?",
    a: "Über bessere Größenangaben am Produkt, echte Maßtabellen statt allgemeiner Angaben, Bilder mit erkennbarem Material und Passform sowie klare Angaben zu Schnitt und Trageeigenschaften. Was in Ihrem Shop wirkt, zeigt erst die Auswertung Ihrer Retourengründe.",
  },
  {
    q: "Was kostet ein Modeshop auf Shopify?",
    a: "Der Aufbau richtet sich nach Sortimentsgröße, Varianten und Individualität und wird als Festpreis vereinbart. Einzelne Anpassungen rechne ich mit 110 € netto pro Stunde ab.",
  },
  {
    q: "Können bestehende Produktdaten übernommen werden?",
    a: "In der Regel ja, über Importe aus vorhandenen Systemen oder Tabellen. Wie sauber das läuft, hängt von der Qualität der Ausgangsdaten ab.",
  },
];

export const Route = createFileRoute("/shopify-fashion-shop")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({ title: TITLE, description: DESCRIPTION, path: PATH, faqs }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Branche · Mode"
      title="Shopify Fashion Shop: worauf es bei Mode wirklich ankommt"
      intro="Modeshops scheitern selten am Design. Sie scheitern an unklaren Größen, unübersichtlichen Kollektionen und langen Wegen auf dem Handy. Diese Übersicht zeigt die Stellen, die im Alltag den Unterschied machen."
      source="shopify-fashion-shop"
      sections={[
        {
          h: "Varianten und Größen sauber aufbauen",
          p: [
            "Größe, Farbe und Schnitt gehören als Optionen an das Produkt, nicht in getrennte Produkte. Sonst zerfällt die Kollektion in Dubletten, und Bestand sowie Auswertung stimmen nicht mehr.",
            "Wichtig ist außerdem, ausverkaufte Größen erkennbar zu machen, statt sie einfach auszublenden.",
          ],
        },
        {
          h: "Retouren beginnen auf der Produktseite",
          p: [
            "Die häufigste Rücksendegrund bei Mode ist die Passform. Echte Maßtabellen, Angaben zur Trägergröße auf den Fotos, Materialangaben und Hinweise zum Schnitt beantworten die Frage vor der Bestellung.",
          ],
          list: [
            "Maßtabelle direkt am Produkt statt auf einer Extraseite",
            "Angabe, welche Größe die abgebildete Person trägt",
            "Material, Dehnbarkeit, Pflege in wenigen Zeilen",
            "Retourengründe auswerten und die häufigsten gezielt angehen",
          ],
        },
        {
          h: "Kollektionen und Navigation",
          p: [
            "Kunden suchen nach Anlass, Kategorie und Größe, nicht nach Ihrer internen Sortimentslogik. Filter für Größe, Farbe und Preis gehören bei Mode zur Grundausstattung, ebenso saubere Sortierung bei Sale-Aktionen.",
          ],
        },
        {
          h: "Mobil zuerst",
          p: [
            "Bei Mode kommt der Großteil der Besucher vom Handy. Große Bilder, schnelle Größenauswahl mit ausreichend großen Flächen und ein kurzer Weg in den Warenkorb sind dort wichtiger als jeder Effekt.",
          ],
        },
        {
          h: "Was ich übernehme",
          p: [
            "Aufbau des Shops mit Varianten, Kollektionen und Filtern, Anpassungen am Theme, Produktimporte sowie die Optimierung von Produktseite, Warenkorb und mobilen Kaufwegen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/shopify-conversion-optimierung", label: "Conversion-Optimierung" },
        { to: "/cases", label: "Kundenprojekte" },
      ]}
    />
  );
}
